import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Grid, MenuItem,
  TextField, Button, Stack, IconButton,
  Tooltip, Skeleton, Chip,
  OutlinedInput, InputAdornment, Tab, Tabs,
} from "@mui/material";
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, LineChart, Line,
} from "recharts";
import TrendingUpIcon           from "@mui/icons-material/TrendingUp";
import TrendingDownIcon         from "@mui/icons-material/TrendingDown";
import CalendarTodayIcon        from "@mui/icons-material/CalendarToday";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import DateRangeIcon            from "@mui/icons-material/DateRange";
import SearchIcon               from "@mui/icons-material/Search";
import CloseIcon                from "@mui/icons-material/Close";
import ArrowRightAltIcon        from "@mui/icons-material/ArrowRightAlt";
import AssessmentIcon           from "@mui/icons-material/Assessment";
import CheckCircleIcon          from "@mui/icons-material/CheckCircleOutlined";
import HourglassEmptyIcon       from "@mui/icons-material/HourglassEmptyOutlined";
import PaidIcon                 from "@mui/icons-material/AccountBalanceWalletOutlined";
import DownloadIcon             from "@mui/icons-material/FileDownloadOutlined";
import InventoryIcon            from "@mui/icons-material/InventoryOutlined";
import CheckIcon                from "@mui/icons-material/CheckCircleOutlined";
import ClockIcon                from "@mui/icons-material/AccessTimeOutlined";
import CancelIcon               from "@mui/icons-material/CancelOutlined";

import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useForkRef } from "@mui/material/utils";
import { usePickerContext, useSplitFieldProps } from "@mui/x-date-pickers";

import api from "../../../services/api";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  ink:        "#111827",
  slate:      "#374151",
  muted:      "#6b7280",
  faint:      "#9ca3af",
  border:     "#e5e7eb",
  borderSoft: "#f3f4f6",
  amber:      "#d97706",
  amberLight: "#fffbeb",
  blue:       "#2563eb",
  blueLight:  "#eff6ff",
  violet:     "#7c3aed",
  emerald:    "#059669",
  rose:       "#dc2626",
  approved:   { text: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
  rejected:   { text: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  pending:    { text: "#d97706", bg: "#fffbeb", border: "#fde68a" },
};

const CARD_RADIUS = "14px";
const CARD_BORDER = `1px solid ${C.border}`;
const CARD_BG     = "#ffffff";

const cardSx = {
  backgroundColor: CARD_BG,
  borderRadius:    CARD_RADIUS,
  border:          CARD_BORDER,
  transition:      "box-shadow .2s ease, transform .2s ease",
  "&:hover":       { boxShadow: "0 4px 20px rgba(0,0,0,0.07)", transform: "translateY(-1px)" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (v) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency", currency: "IDR", maximumFractionDigits: 0,
  }).format(v || 0);

const fmtShort = (v) => {
  if (!v) return "Rp 0";
  if (v >= 1_000_000_000) return `Rp ${(v / 1_000_000_000).toFixed(1)}M`;
  if (v >= 1_000_000)     return `Rp ${(v / 1_000_000).toFixed(1)}Jt`;
  if (v >= 1_000)         return `Rp ${(v / 1_000).toFixed(0)}Rb`;
  return `Rp ${v}`;
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "-";

const REQUEST_TYPE_LABEL = { biasa: "Biasa", segera: "Segera", mendesak: "Mendesak" };
const REQUEST_TYPE_COLOR = {
  biasa:    { bg: "#f3f4f6", color: "#6b7280" },
  segera:   { bg: "#fffbeb", color: "#d97706" },
  mendesak: { bg: "#fef2f2", color: "#dc2626" },
};

const STATUS_MAP = {
  Disetujui: { bg: C.approved.bg, color: C.approved.text, border: C.approved.border },
  Ditolak:   { bg: C.rejected.bg, color: C.rejected.text, border: C.rejected.border },
  Pending:   { bg: C.pending.bg,  color: C.pending.text,  border: C.pending.border  },
};

// ─── ButtonField ──────────────────────────────────────────────────────────────

function ButtonField(props) {
  const { forwardedProps } = useSplitFieldProps(props, "date");
  const pickerContext = usePickerContext();
  const handleRef = useForkRef(pickerContext.triggerRef, pickerContext.rootRef);
  const hasValue = pickerContext.value != null;
  const valueStr = hasValue ? pickerContext.value.format(pickerContext.fieldFormat) : null;

  return (
    <Button
      {...forwardedProps}
      variant="outlined"
      ref={handleRef}
      size="small"
      startIcon={<CalendarTodayRoundedIcon sx={{ fontSize: "13px !important" }} />}
      onClick={() => pickerContext.setOpen((prev) => !prev)}
      sx={{
        borderRadius: "9px", fontSize: "0.78rem", textTransform: "none",
        fontWeight: 500, minWidth: "fit-content", height: 34, px: 1.5,
        borderColor: hasValue ? C.amber : C.border,
        color: hasValue ? C.amber : C.slate,
        bgcolor: hasValue ? C.amberLight : "#fff",
        "&:hover": { borderColor: hasValue ? C.amber : "#9ca3af", bgcolor: hasValue ? C.amberLight : "#f9fafb" },
      }}
    >
      {pickerContext.label ?? valueStr ?? "Pilih Tanggal"}
    </Button>
  );
}

// ─── Sparkline ────────────────────────────────────────────────────────────────

function Sparkline({ data, color }) {
  return (
    <ResponsiveContainer width="100%" height={44}>
      <LineChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub, badge, up, accent, sparkData, sparkColor, loading }) {
  const accentColor = accent || C.blue;
  return (
    <Box sx={{ ...cardSx, p: 2.5, display: "flex", flexDirection: "column", gap: 1.5, height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: "10px",
          backgroundColor: accentColor + "18",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: accentColor,
        }}>
          {icon}
        </Box>
        {!loading && badge && (
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.3,
            background: up ? "#f0fdf4" : "#fef2f2",
            borderRadius: "20px", px: 0.9, py: 0.3,
          }}>
            {up
              ? <TrendingUpIcon sx={{ fontSize: 10, color: "#16a34a" }} />
              : <TrendingDownIcon sx={{ fontSize: 10, color: C.rose }} />}
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: up ? "#16a34a" : C.rose }}>
              {badge}
            </Typography>
          </Box>
        )}
      </Box>

      {loading ? (
        <>
          <Skeleton width="55%" height={34} />
          <Skeleton width="70%" height={14} />
        </>
      ) : (
        <>
          <Typography sx={{ fontSize: "1.8rem", fontWeight: 800, color: C.ink, letterSpacing: "-0.04em", lineHeight: 1 }}>
            {value}
          </Typography>
          <Box>
            <Typography sx={{ fontSize: "0.75rem", color: C.muted, fontWeight: 500 }}>{label}</Typography>
            {sub && <Typography sx={{ fontSize: "0.65rem", color: C.faint, mt: 0.3 }}>{sub}</Typography>}
          </Box>
        </>
      )}

      {sparkData && !loading && (
        <Box sx={{ mt: "auto" }}>
          <Sparkline data={sparkData} color={sparkColor || C.blue} />
        </Box>
      )}
    </Box>
  );
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

function StatusBadge({ label }) {
  const s = STATUS_MAP[label] || { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb" };
  return (
    <Chip
      label={label || "-"}
      size="small"
      sx={{
        height: 20, fontSize: "0.63rem", fontWeight: 700,
        backgroundColor: s.bg, color: s.color,
        border: `1px solid ${s.border}`,
        borderRadius: "6px",
        "& .MuiChip-label": { px: "7px" },
      }}
    />
  );
}

// ─── ChartTooltip ─────────────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: "#fff", border: "1px solid #e5e7eb",
      borderRadius: "12px", p: 1.8, boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
      minWidth: 140,
    }}>
      <Typography sx={{ fontSize: "0.73rem", fontWeight: 700, color: "#374151", mb: 1 }}>{label}</Typography>
      {payload.map((p) => (
        <Stack key={p.name} direction="row" alignItems="center" spacing={1} mb={0.5}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.7rem", color: "#6b7280", flex: 1 }}>{p.name}</Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#111827" }}>{p.value}</Typography>
        </Stack>
      ))}
    </Box>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function LaporanPengadaan() {
  const currentYear = new Date().getFullYear();

  const [filters, setFilters] = useState({
    period: "monthly", unit_id: "", status: "",
    date: "", date_from: "", date_to: "",
  });

  const [selectedDate, setSelectedDate] = useState(null);
  const [dateFrom,     setDateFrom]     = useState(null);
  const [dateTo,       setDateTo]       = useState(null);

  const [data,          setData]          = useState(null);
  const [perUnit,       setPerUnit]       = useState([]);
  const [topItems,      setTopItems]      = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [page,          setPage]          = useState(1);
  const [search,        setSearch]        = useState("");
  const [topTab,        setTopTab]        = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page };
      const [r1, r2, r3] = await Promise.all([
        api.get("/reports/procurement",           { params }),
        api.get("/reports/procurement/per-unit",  { params: { year: currentYear } }),
        api.get("/reports/procurement/top-items", { params: { year: currentYear, limit: 6 } }),
      ]);
      setData(r1.data);
      setPerUnit(r2.data || []);
      setTopItems(r3.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Date handlers ──────────────────────────────────────────────────────────

  const handleSingleDateChange = (v) => {
    setSelectedDate(v); setDateFrom(null); setDateTo(null);
    setFilters((p) => v?.isValid()
      ? { ...p, date: v.format("YYYY-MM-DD"), date_from: "", date_to: "" }
      : { ...p, date: "" });
  };

  const handleDateFromChange = (v) => {
    setDateFrom(v); setSelectedDate(null);
    setFilters((p) => v?.isValid()
      ? { ...p, date: "", date_from: v.format("YYYY-MM-DD") }
      : { ...p, date_from: "" });
  };

  const handleDateToChange = (v) => {
    setDateTo(v); setSelectedDate(null);
    setFilters((p) => v?.isValid()
      ? { ...p, date: "", date_to: v.format("YYYY-MM-DD") }
      : { ...p, date_to: "" });
  };

  const handleResetAll = () => {
    setSelectedDate(null); setDateFrom(null); setDateTo(null);
    setFilters((p) => ({ ...p, date: "", date_from: "", date_to: "", unit_id: "", status: "" }));
  };

  // ── Export CSV ─────────────────────────────────────────────────────────────

  const handleExportCSV = async () => {
    setLoadingExport(true);
    try {
      const res = await api.get("/reports/procurement", { params: { ...filters, page: 1, per_page: 9999 } });
      const rows = res.data?.rows?.data || [];
      const summary = res.data?.summary || {};
      
      if (!rows.length) return;

      // ─── Trik HTML to Excel (biar rapi ada warna & border) ───
      let html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8">
          <style>
            .header { background-color: #1e293b; color: #ffffff; font-weight: bold; text-align: center; }
            .title { font-size: 18pt; font-weight: bold; text-align: center; color: #1e293b; }
            .summary-label { background-color: #f8fafc; font-weight: bold; color: #64748b; }
            .summary-value { font-weight: bold; color: #1e293b; }
            .table-cell { border: 1px solid #e2e8f0; padding: 5px; }
            .th { background-color: #f1f5f9; font-weight: bold; text-align: center; border: 1px solid #cbd5e1; }
            .req-group { background-color: #fdfcfb; font-weight: bold; vertical-align: middle; }
          </style>
        </head>
        <body>
          <table>
            <tr><td colspan="10" class="title">LAPORAN PENGADAAN SARANA PRASARANA</td></tr>
            <tr><td colspan="10" style="text-align: center;">BAS-LPKIA · Tanggal Cetak: ${dayjs().format('DD MMMM YYYY HH:mm')}</td></tr>
            <tr><td colspan="10" style="text-align: center;">Periode: ${activeDateLabel || 'Semua Waktu'}</td></tr>
            <tr><td colspan="10"></td></tr>
            
            <tr><td colspan="3" class="summary-label table-cell">RINGKASAN LAPORAN</td><td colspan="7" class="table-cell"></td></tr>
            <tr><td class="summary-label table-cell">Total Pengajuan</td><td class="summary-value table-cell">${summary.total_pengajuan || 0}</td><td colspan="8" class="table-cell"></td></tr>
            <tr><td class="summary-label table-cell">Total Nilai</td><td class="summary-value table-cell">Rp ${new Intl.NumberFormat('id-ID').format(summary.total_nilai || 0)}</td><td colspan="8" class="table-cell"></td></tr>
            <tr><td class="summary-label table-cell">Disetujui</td><td class="table-cell" style="color: #059669; font-weight: bold;">${summary.disetujui || 0}</td><td colspan="8" class="table-cell"></td></tr>
            <tr><td class="summary-label table-cell">Pending</td><td class="table-cell" style="color: #d97706; font-weight: bold;">${summary.pending || 0}</td><td colspan="8" class="table-cell"></td></tr>
            <tr><td class="summary-label table-cell">Ditolak</td><td class="table-cell" style="color: #dc2626; font-weight: bold;">${summary.ditolak || 0}</td><td colspan="8" class="table-cell"></td></tr>
            <tr><td colspan="10"></td></tr>

            <tr>
              <td class="th">No. Pengajuan</td>
              <td class="th">Tanggal</td>
              <td class="th">Department</td>
              <td class="th">Pengaju</td>
              <td class="th">Jenis</td>
              <td class="th">Status</td>
              <td class="th" style="background-color: #eff6ff;">Nama Barang</td>
              <td class="th" style="background-color: #eff6ff;">Qty</td>
              <td class="th" style="background-color: #eff6ff;">Harga Satuan</td>
              <td class="th" style="background-color: #eff6ff;">Subtotal</td>
            </tr>
      `;

      rows.forEach((r) => {
        if (r.items && r.items.length > 0) {
          r.items.forEach((item, idx) => {
            html += `
              <tr>
                <td class="table-cell ${idx === 0 ? 'req-group' : ''}">${idx === 0 ? r.request_number : ''}</td>
                <td class="table-cell">${idx === 0 ? dayjs(r.request_date || r.created_at).format('DD/MM/YYYY') : ''}</td>
                <td class="table-cell">${idx === 0 ? (r.department || '') : ''}</td>
                <td class="table-cell">${idx === 0 ? (r.requester_name || '') : ''}</td>
                <td class="table-cell">${idx === 0 ? (REQUEST_TYPE_LABEL[r.request_type] || '-') : ''}</td>
                <td class="table-cell" style="text-align: center;">${idx === 0 ? (r.status?.label || '-') : ''}</td>
                <td class="table-cell">${item.item_name || ''}</td>
                <td class="table-cell" style="text-align: center;">${item.quantity || 0}</td>
                <td class="table-cell">Rp ${new Intl.NumberFormat('id-ID').format(item.price || 0)}</td>
                <td class="table-cell" style="font-weight: bold;">Rp ${new Intl.NumberFormat('id-ID').format(item.subtotal || 0)}</td>
              </tr>
            `;
          });
          // Spacer row for each request
          html += `<tr><td colspan="10" style="background-color: #f8fafc; height: 2px; border-top: 1px solid #e2e8f0;"></td></tr>`;
        } else {
          html += `
            <tr>
              <td class="table-cell req-group">${r.request_number}</td>
              <td class="table-cell">${dayjs(r.request_date || r.created_at).format('DD/MM/YYYY')}</td>
              <td class="table-cell">${r.department || ""}</td>
              <td class="table-cell">${r.requester_name || ""}</td>
              <td class="table-cell">${REQUEST_TYPE_LABEL[r.request_type] || "-"}</td>
              <td class="table-cell" style="text-align: center;">${r.status?.label || "-"}</td>
              <td class="table-cell">-</td>
              <td class="table-cell" style="text-align: center;">0</td>
              <td class="table-cell">Rp 0</td>
              <td class="table-cell" style="font-weight: bold;">Rp ${new Intl.NumberFormat('id-ID').format(r.total_amount || 0)}</td>
            </tr>
          `;
        }
      });

      html += `
          </table>
        </body>
        </html>
      `;

      const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Laporan_Pengadaan_${dayjs().format('YYYYMMDD_HHmm')}.xls`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export error:", e);
    } finally {
      setLoadingExport(false);
    }
  };

  // ── Derived state ──────────────────────────────────────────────────────────

  const hasDateFilter   = selectedDate?.isValid();
  const hasRangeFilter  = dateFrom?.isValid() || dateTo?.isValid();
  const hasActiveFilter = hasDateFilter || hasRangeFilter;

  const activeDateLabel = (() => {
    if (hasDateFilter) return selectedDate.format("DD MMMM YYYY");
    if (hasRangeFilter) {
      if (dateFrom?.isValid() && dateTo?.isValid())
        return `${dateFrom.format("DD MMM YYYY")} \u2013 ${dateTo.format("DD MMM YYYY")}`;
      if (dateFrom?.isValid()) return `Dari ${dateFrom.format("DD MMM YYYY")}`;
      if (dateTo?.isValid())   return `Sampai ${dateTo.format("DD MMM YYYY")}`;
    }
    return "";
  })();

  const chartData = data?.chart_data
    ? data.chart_data.labels.map((label, i) => ({
        label,
        Disetujui: data.chart_data.disetujui[i] ?? 0,
        Ditolak:   data.chart_data.ditolak[i]   ?? 0,
        Pending:   data.chart_data.pending[i]   ?? 0,
      }))
    : [];

  const sparkTotal    = chartData.map((d) => ({ v: d.Disetujui + d.Ditolak + d.Pending }));
  const sparkApproved = chartData.map((d) => ({ v: d.Disetujui }));
  const sparkPending  = chartData.map((d) => ({ v: d.Pending }));

  const tableRows = (data?.rows?.data || []).filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.request_number?.toLowerCase().includes(q) ||
      r.department?.toLowerCase().includes(q) ||
      r.requester_name?.toLowerCase().includes(q)
    );
  });

  const statSubLabel = (() => {
    if (hasDateFilter) return selectedDate.format("DD MMM YYYY");
    if (hasRangeFilter && dateFrom?.isValid() && dateTo?.isValid())
      return `${dateFrom.format("DD MMM")} \u2013 ${dateTo.format("DD MMM YYYY")}`;
    if (hasRangeFilter && dateFrom?.isValid()) return `Dari ${dateFrom.format("DD MMM YYYY")}`;
    if (hasRangeFilter && dateTo?.isValid())   return `Sampai ${dateTo.format("DD MMM YYYY")}`;
    return `Tahun ${currentYear}`;
  })();

  const statCards = [
    {
      icon: <AssessmentIcon sx={{ fontSize: 19 }} />,
      label: "Total Pengajuan",
      value: loading ? "-" : String(data?.summary?.total_pengajuan ?? 0),
      sub: statSubLabel, accent: "#2563eb",
      sparkData: sparkTotal, sparkColor: "#2563eb",
    },
    {
      icon: <PaidIcon sx={{ fontSize: 19 }} />,
      label: "Total Nilai",
      value: loading ? "-" : fmtShort(data?.summary?.total_nilai),
      sub: loading ? "" : fmt(data?.summary?.total_nilai),
      accent: "#7c3aed", sparkData: sparkApproved, sparkColor: "#7c3aed",
    },
    {
      icon: <CheckCircleIcon sx={{ fontSize: 19 }} />,
      label: "Disetujui",
      value: loading ? "-" : String(data?.summary?.disetujui ?? 0),
      accent: "#059669", sparkData: sparkApproved, sparkColor: "#059669",
    },
    {
      icon: <HourglassEmptyIcon sx={{ fontSize: 19 }} />,
      label: "Pending",
      value: loading ? "-" : String(data?.summary?.pending ?? 0),
      sub: `Ditolak: ${data?.summary?.ditolak ?? 0}`,
      accent: "#d97706", sparkData: sparkPending, sparkColor: "#d97706",
    },
  ];

  const summaryRows = [
    { label: "Total Pengajuan",  value: String(data?.summary?.total_pengajuan ?? 0) },
    { label: "Total Nilai",      value: fmtShort(data?.summary?.total_nilai) },
    { label: "Rata-rata Nilai",  value: data?.summary?.total_pengajuan
        ? fmtShort((data.summary.total_nilai || 0) / data.summary.total_pengajuan) : "Rp 0" },
    { label: "Total Disetujui",  value: String(data?.summary?.disetujui ?? 0) },
    { label: "Total Ditolak",    value: String(data?.summary?.ditolak ?? 0) },
  ];

  return (
    <Box>

      {/* ── Page Header ── */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: "#111827", letterSpacing: "-0.03em", lineHeight: 1 }}>
            Laporan Pengadaan
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "#9ca3af", mt: 0.4 }}>
            Statistik &amp; rekap pengajuan sarana prasarana
          </Typography>
        </Box>
        <Button
          variant="outlined" size="small"
          startIcon={<DownloadIcon sx={{ fontSize: "15px !important" }} />}
          onClick={handleExportCSV}
          disabled={loadingExport || loading}
          sx={{
            borderRadius: "9px", fontSize: "0.78rem", textTransform: "none",
            fontWeight: 600, height: 36, px: 2,
            borderColor: "#e5e7eb", color: "#374151",
            "&:hover": { borderColor: "#2563eb", color: "#2563eb", bgcolor: "#eff6ff" },
          }}
        >
          {loadingExport ? "Mengekspor..." : "Export CSV"}
        </Button>
      </Box>

      {/* ── Filter Row ── */}
      <Stack direction="row" spacing={1} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={selectedDate}
            label={selectedDate?.isValid() ? selectedDate.format("DD MMM YYYY") : "Tanggal"}
            onChange={handleSingleDateChange}
            slots={{ field: ButtonField }}
            views={["year", "month", "day"]}
          />
        </LocalizationProvider>

        <Stack direction="row" spacing={0.75} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={dateFrom}
              label={dateFrom?.isValid() ? dateFrom.format("DD MMM YYYY") : "Dari Tanggal"}
              onChange={handleDateFromChange}
              slots={{ field: ButtonField }}
              views={["year", "month", "day"]}
              maxDate={dateTo || undefined}
            />
          </LocalizationProvider>
          <ArrowRightAltIcon sx={{ fontSize: 16, color: "#d1d5db", flexShrink: 0 }} />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={dateTo}
              label={dateTo?.isValid() ? dateTo.format("DD MMM YYYY") : "Ke Tanggal"}
              onChange={handleDateToChange}
              slots={{ field: ButtonField }}
              views={["year", "month", "day"]}
              minDate={dateFrom || undefined}
            />
          </LocalizationProvider>
        </Stack>

        <Box sx={{ width: "1px", height: 24, bgcolor: "#e5e7eb", mx: 0.5 }} />

        <TextField
          select size="small" value={filters.unit_id}
          onChange={(e) => setFilters((p) => ({ ...p, unit_id: e.target.value }))}
          sx={{
            minWidth: 160,
            "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.8rem", height: 36 },
            "& fieldset": { borderColor: "#e5e7eb" },
          }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value=""><em style={{ color: "#9ca3af", fontStyle: "normal" }}>Semua Department</em></MenuItem>
          {perUnit.map((u) => (
            <MenuItem key={u.unit} value={u.unit} sx={{ fontSize: "0.8rem" }}>{u.unit}</MenuItem>
          ))}
        </TextField>

        <TextField
          select size="small" value={filters.status}
          onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
          sx={{
            minWidth: 130,
            "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.8rem", height: 36 },
            "& fieldset": { borderColor: "#e5e7eb" },
          }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value=""><em style={{ color: "#9ca3af", fontStyle: "normal" }}>Semua Status</em></MenuItem>
          <MenuItem value="1" sx={{ fontSize: "0.8rem" }}>Pending</MenuItem>
          <MenuItem value="2" sx={{ fontSize: "0.8rem" }}>Disetujui</MenuItem>
          <MenuItem value="3" sx={{ fontSize: "0.8rem" }}>Ditolak</MenuItem>
        </TextField>

        {(hasActiveFilter || filters.unit_id || filters.status) && (
          <Tooltip title="Reset semua filter">
            <IconButton
              size="small" onClick={handleResetAll}
              sx={{
                border: "1px solid #e5e7eb", borderRadius: "8px",
                width: 36, height: 36, color: "#9ca3af",
                "&:hover": { color: "#dc2626", borderColor: "#fecaca", bgcolor: "#fef2f2" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* ── Active Filter Badge ── */}
      {hasActiveFilter && (
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: 0.8,
          px: 1.2, py: 0.5, mb: 2,
          border: "1px solid #bfdbfe", borderRadius: "8px", bgcolor: "#eff6ff",
        }}>
          {hasRangeFilter
            ? <DateRangeIcon sx={{ fontSize: 12, color: "#2563eb" }} />
            : <CalendarTodayIcon sx={{ fontSize: 12, color: "#2563eb" }} />}
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#1d4ed8" }}>
            {activeDateLabel}
          </Typography>
          <Box onClick={handleResetAll} sx={{ display: "flex", cursor: "pointer", color: "#93c5fd", "&:hover": { color: "#1d4ed8" } }}>
            <CloseIcon sx={{ fontSize: 12 }} />
          </Box>
        </Box>
      )}

      {/* ── Stat Cards ── */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {statCards.map((card, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard {...card} loading={loading} />
          </Grid>
        ))}
      </Grid>

      {/* ── Charts Row ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>

        {/* Area Chart */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Box sx={{ backgroundColor: CARD_BG, border: CARD_BORDER, borderRadius: CARD_RADIUS, p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
              <Box>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#111827" }}>Tren Pengajuan</Typography>
                <Typography sx={{ fontSize: "0.73rem", color: "#9ca3af", mt: 0.25 }}>
                  {hasActiveFilter ? activeDateLabel : `${currentYear} \u2014 semua status`}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {["monthly", "quarterly", "yearly"].map((p) => (
                  <Button
                    key={p} size="small"
                    onClick={() => setFilters((f) => ({ ...f, period: p }))}
                    sx={{
                      minWidth: 0, px: 1.4, height: 26, borderRadius: "7px",
                      fontSize: "0.68rem", fontWeight: 600, textTransform: "none",
                      ...(filters.period === p
                        ? { bgcolor: "#111827", color: "#fff", "&:hover": { bgcolor: "#374151" } }
                        : { color: "#9ca3af", "&:hover": { bgcolor: "#f3f4f6", color: "#374151" } }),
                    }}
                  >
                    {p === "monthly" ? "Bln" : p === "quarterly" ? "Krtl" : "Thn"}
                  </Button>
                ))}
              </Stack>
            </Box>

            <Stack direction="row" spacing={2} mb={2}>
              {[
                { key: "Disetujui", color: "#059669", bg: "#ecfdf5" },
                { key: "Pending",   color: "#d97706", bg: "#fffbeb" },
                { key: "Ditolak",   color: "#dc2626", bg: "#fef2f2" },
              ].map(({ key, color, bg }) => (
                <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 0.6, px: 1, py: 0.4, borderRadius: "6px", bgcolor: bg }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color }} />
                  <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color }}>{key}</Typography>
                </Box>
              ))}
            </Stack>

            {chartData.length === 0 && !loading ? (
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 4, gap: 1 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: "12px", bgcolor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AssessmentIcon sx={{ fontSize: 22, color: "#d1d5db" }} />
                </Box>
                <Typography sx={{ fontSize: "0.78rem", color: "#9ca3af" }}>Belum ada data tren</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gApproved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#059669" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#059669" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gPending" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#d97706" stopOpacity={0.22} />
                      <stop offset="100%" stopColor="#d97706" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gRejected" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#dc2626" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#dc2626" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} dy={4} />
                  <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
                  <RTooltip content={<ChartTooltip />} cursor={{ stroke: "#e5e7eb", strokeWidth: 1, strokeDasharray: "4 2" }} />
                  <Area type="monotone" dataKey="Disetujui" stroke="#059669" strokeWidth={2.5} fill="url(#gApproved)" dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: "#059669" }} />
                  <Area type="monotone" dataKey="Pending"   stroke="#d97706" strokeWidth={2.5} fill="url(#gPending)"  dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: "#d97706" }} />
                  <Area type="monotone" dataKey="Ditolak"   stroke="#dc2626" strokeWidth={2.5} fill="url(#gRejected)" dot={false} activeDot={{ r: 4, strokeWidth: 0, fill: "#dc2626" }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Grid>

        {/* Bar Chart — Nilai per Department */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ backgroundColor: CARD_BG, border: CARD_BORDER, borderRadius: CARD_RADIUS, p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2.5 }}>
              <Box>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#111827" }}>Nilai per Department</Typography>
                <Typography sx={{ fontSize: "0.73rem", color: "#9ca3af", mt: 0.25 }}>
                  Total nilai pengadaan &middot; {currentYear}
                </Typography>
              </Box>
              <Box sx={{ px: 1, py: 0.3, borderRadius: "6px", bgcolor: "#eff6ff", border: "1px solid #bfdbfe" }}>
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#2563eb" }}>
                  TOP {Math.min(perUnit.length, 6)}
                </Typography>
              </Box>
            </Box>

            {loading ? (
              [1, 2, 3, 4, 5].map((i) => (
                <Box key={i} sx={{ mb: 2.5 }}>
                  <Skeleton width="60%" height={14} sx={{ mb: 0.5 }} />
                  <Skeleton width="100%" height={8} sx={{ borderRadius: 4 }} />
                </Box>
              ))
            ) : perUnit.length === 0 ? (
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, py: 3 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: "12px", bgcolor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AssessmentIcon sx={{ fontSize: 22, color: "#d1d5db" }} />
                </Box>
                <Typography sx={{ fontSize: "0.78rem", color: "#9ca3af" }}>Belum ada data</Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1 }}>
                {perUnit.slice(0, 6).map((row, i) => {
                  const maxNilai = perUnit[0]?.total_nilai || 1;
                  const pct      = Math.round((row.total_nilai / maxNilai) * 100);
                  const PALETTE  = ["#2563eb", "#7c3aed", "#059669", "#d97706", "#dc2626", "#0ea5e9"];
                  const color    = PALETTE[i] || "#2563eb";
                  return (
                    <Box key={i} sx={{ mb: i < 5 ? 2.2 : 0 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.7 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0 }}>
                          <Box sx={{ width: 18, height: 18, borderRadius: "5px", bgcolor: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Typography sx={{ fontSize: "0.6rem", fontWeight: 800, color }}>{i + 1}</Typography>
                          </Box>
                          <Typography noWrap sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151" }}>{row.unit || "-"}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
                          <Typography sx={{ fontSize: "0.65rem", color: "#9ca3af" }}>{row.jumlah ?? 0} pengajuan</Typography>
                          <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: "#111827" }}>{fmtShort(row.total_nilai)}</Typography>
                        </Stack>
                      </Box>
                      <Box sx={{ height: 6, borderRadius: 3, bgcolor: "#f3f4f6", overflow: "hidden" }}>
                        <Box sx={{ height: "100%", borderRadius: 3, width: `${pct}%`, background: `linear-gradient(90deg, ${color}, ${color}99)`, transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)" }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {!loading && perUnit.length > 0 && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af" }}>{perUnit.length} department</Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#374151" }}>
                  Total: {fmtShort(perUnit.reduce((s, r) => s + (parseFloat(r.total_nilai) || 0), 0))}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* ── Bottom Row ── */}
      <Grid container spacing={2}>

        {/* Tabel Detail */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Box sx={{ backgroundColor: CARD_BG, border: CARD_BORDER, borderRadius: CARD_RADIUS }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#111827" }}>Daftar Pengajuan</Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.2 }}>
                  {data?.rows?.total ?? 0} pengajuan ditemukan
                  {hasActiveFilter && ` \u00b7 ${activeDateLabel}`}
                </Typography>
              </Box>
              <OutlinedInput
                size="small" placeholder="Cari pengajuan..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                startAdornment={<InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: "#9ca3af" }} /></InputAdornment>}
                sx={{
                  borderRadius: "8px", fontSize: "0.78rem",
                  "& input": { py: "6px" }, width: 190,
                  "& fieldset": { borderColor: "#e5e7eb" },
                }}
              />
            </Box>

            <Box sx={{
              display: "grid", gridTemplateColumns: "2fr 1.4fr 1.1fr 0.9fr 1.1fr 0.7fr 0.9fr",
              gap: 1, px: 3, py: 1,
              borderTop: "1px solid #f3f4f6", borderBottom: "1px solid #f3f4f6",
            }}>
              {["No. Pengajuan", "Department", "Pengaju", "Tgl", "Nilai", "Jenis", "Status"].map((h) => (
                <Typography key={h} sx={{ fontSize: "0.67rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {h}
                </Typography>
              ))}
            </Box>

            {loading ? (
              [1, 2, 3, 4, 5].map((i) => (
                <Box key={i} sx={{ px: 3, py: 1.5, borderBottom: "1px solid #f9fafb" }}>
                  <Skeleton height={16} width="85%" />
                </Box>
              ))
            ) : tableRows.length === 0 ? (
              <Box sx={{ py: 6, textAlign: "center" }}>
                <Typography sx={{ color: "#9ca3af", fontSize: "0.8rem" }}>
                  Tidak ada data pengajuan{hasActiveFilter ? ` \u00b7 ${activeDateLabel}` : ""}
                </Typography>
              </Box>
            ) : (
              tableRows.map((row, i) => {
                const rtc = REQUEST_TYPE_COLOR[row.request_type] || REQUEST_TYPE_COLOR.biasa;
                return (
                  <Box key={row.id} sx={{
                    display: "grid", gridTemplateColumns: "2fr 1.4fr 1.1fr 0.9fr 1.1fr 0.7fr 0.9fr",
                    gap: 1, px: 3, py: 1.4, alignItems: "center",
                    borderBottom: i < tableRows.length - 1 ? "1px solid #f9fafb" : "none",
                    "&:hover": { bgcolor: "#f9fafb" }, transition: "background 0.1s",
                  }}>
                    <Typography sx={{ fontFamily: "monospace", fontSize: "0.73rem", color: "#6b7280" }}>{row.request_number}</Typography>
                    <Typography noWrap sx={{ fontSize: "0.78rem", color: "#374151", fontWeight: 600 }}>{row.department}</Typography>
                    <Typography noWrap sx={{ fontSize: "0.78rem", color: "#6b7280" }}>{row.requester_name}</Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", whiteSpace: "nowrap" }}>{fmtDate(row.request_date || row.created_at)}</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#111827", whiteSpace: "nowrap" }}>{fmt(row.total_amount)}</Typography>
                    <Box>
                      <Box component="span" sx={{ px: "7px", py: "2px", borderRadius: "6px", fontSize: "0.63rem", fontWeight: 700, bgcolor: rtc.bg, color: rtc.color }}>
                        {REQUEST_TYPE_LABEL[row.request_type] || "-"}
                      </Box>
                    </Box>
                    <Box><StatusBadge label={row.status?.name} /></Box>
                  </Box>
                );
              })
            )}

            {!loading && (data?.rows?.last_page ?? 1) > 1 && (
              <Box sx={{ px: 3, py: 1.2, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #f3f4f6" }}>
                <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af" }}>
                  Hal {page} dari {data.rows.last_page} &middot; {data.rows.total} total
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <Button size="small" disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                    sx={{ minWidth: 30, height: 28, borderRadius: "7px", border: "1px solid #e5e7eb", fontSize: "0.8rem", color: "#6b7280" }}>
                    &lsaquo;
                  </Button>
                  {Array.from({ length: data.rows.last_page }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === data.rows.last_page || Math.abs(p - page) <= 1)
                    .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push("..."); acc.push(p); return acc; }, [])
                    .map((p, idx) => p === "..." ? (
                      <Typography key={`d${idx}`} sx={{ display: "flex", alignItems: "center", px: 0.5, fontSize: "0.72rem", color: "#9ca3af" }}>&hellip;</Typography>
                    ) : (
                      <Button key={p} size="small" onClick={() => setPage(p)}
                        sx={{ minWidth: 30, height: 28, borderRadius: "7px", fontSize: "0.75rem",
                          ...(p === page
                            ? { bgcolor: "#111827", color: "#fff", "&:hover": { bgcolor: "#1f2937" } }
                            : { border: "1px solid #e5e7eb", color: "#6b7280" }) }}>
                        {p}
                      </Button>
                    ))}
                  <Button size="small" disabled={page === data.rows.last_page} onClick={() => setPage((p) => p + 1)}
                    sx={{ minWidth: 30, height: 28, borderRadius: "7px", border: "1px solid #e5e7eb", fontSize: "0.8rem", color: "#6b7280" }}>
                    &rsaquo;
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </Grid>

        {/* ── Panel Kanan — 2 card saja ── */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>

            {/* Card 1: Statistik = Distribusi Status + Ringkasan */}
            <Box sx={{ backgroundColor: CARD_BG, border: CARD_BORDER, borderRadius: CARD_RADIUS, p: 3 }}>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#111827", mb: 2 }}>
                Statistik
              </Typography>

              {/* Status 3 box */}
              <Box sx={{ display: "flex", gap: 1, mb: 2.5 }}>
                {[
                  { key: "disetujui", label: "Disetujui", icon: <CheckIcon sx={{ fontSize: 14 }} />,  color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
                  { key: "pending",   label: "Pending",   icon: <ClockIcon sx={{ fontSize: 14 }} />,  color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
                  { key: "ditolak",   label: "Ditolak",   icon: <CancelIcon sx={{ fontSize: 14 }} />, color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
                ].map((s) => {
                  const val   = data?.summary?.[s.key] ?? 0;
                  const total = data?.summary?.total_pengajuan || 1;
                  const pct   = Math.round((val / total) * 100);
                  return (
                    <Box key={s.key} sx={{
                      flex: 1, bgcolor: s.bg, borderRadius: "10px", p: 1.5,
                      border: `1px solid ${s.border}`,
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3,
                    }}>
                      <Box sx={{ color: s.color }}>{s.icon}</Box>
                      <Typography sx={{ fontSize: "1.3rem", fontWeight: 800, color: s.color, lineHeight: 1.1 }}>
                        {loading ? <Skeleton width={20} /> : val}
                      </Typography>
                      <Typography sx={{ fontSize: "0.6rem", color: s.color, fontWeight: 600 }}>{s.label}</Typography>
                      <Typography sx={{ fontSize: "0.58rem", color: "#9ca3af" }}>{pct}%</Typography>
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ borderTop: "1px solid #f3f4f6", mb: 1.5 }} />

              {/* Ringkasan rows */}
              {summaryRows.map(({ label, value }) => (
                <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.85 }}>
                  <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{label}</Typography>
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#111827" }}>
                    {loading ? <Skeleton width={50} /> : value}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Card 2: Top Barang / Top Department dengan Tabs */}
            <Box sx={{ backgroundColor: CARD_BG, border: CARD_BORDER, borderRadius: CARD_RADIUS, overflow: "hidden" }}>
              <Box sx={{ px: 3, pt: 2.5 }}>
                <Tabs
                  value={topTab}
                  onChange={(_, v) => setTopTab(v)}
                  sx={{
                    minHeight: 0,
                    "& .MuiTabs-indicator": { height: 2, borderRadius: 1, bgcolor: "#111827" },
                    "& .MuiTab-root": {
                      minHeight: 0, py: 0.8, px: 0, mr: 2.5,
                      fontSize: "0.82rem", fontWeight: 600, textTransform: "none",
                      color: "#9ca3af", minWidth: 0,
                      "&.Mui-selected": { color: "#111827" },
                    },
                  }}
                >
                  <Tab label="Top Barang" />
                  <Tab label="Top Department" />
                </Tabs>
                <Box sx={{ borderBottom: "1px solid #f3f4f6", mt: 0 }} />
              </Box>

              <Box sx={{ p: 3 }}>
                {/* Panel: Top Barang */}
                {topTab === 0 && (
                  <>
                    <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mb: 2 }}>
                      Barang paling sering diajukan &middot; {currentYear}
                    </Typography>
                    {loading ? (
                      [1, 2, 3, 4, 5].map((i) => (
                        <Box key={i} sx={{ mb: 1.8 }}>
                          <Skeleton width="65%" height={13} sx={{ mb: 0.5 }} />
                          <Skeleton width="100%" height={6} sx={{ borderRadius: 3 }} />
                        </Box>
                      ))
                    ) : topItems.length === 0 ? (
                      <Box sx={{ py: 3, textAlign: "center" }}>
                        <InventoryIcon sx={{ fontSize: 28, color: "#d1d5db", mb: 0.5 }} />
                        <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af" }}>Belum ada data</Typography>
                      </Box>
                    ) : (
                      topItems.map((item, i) => {
                        const maxFrek = topItems[0]?.frekuensi || 1;
                        const pct     = Math.round((item.frekuensi / maxFrek) * 100);
                        const COLORS  = ["#2563eb", "#7c3aed", "#059669", "#d97706", "#dc2626", "#0ea5e9"];
                        const color   = COLORS[i] || "#2563eb";
                        return (
                          <Box key={i} sx={{ mb: i < topItems.length - 1 ? 1.8 : 0 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, minWidth: 0 }}>
                                <Typography sx={{ fontSize: "0.6rem", fontWeight: 800, color, minWidth: 16, flexShrink: 0 }}>#{i + 1}</Typography>
                                <Typography noWrap sx={{ fontSize: "0.76rem", fontWeight: 600, color: "#374151" }}>{item.item_name}</Typography>
                              </Box>
                              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
                                <Typography sx={{ fontSize: "0.62rem", color: "#9ca3af" }}>qty {item.total_qty}</Typography>
                                <Box sx={{ px: "6px", py: "1px", borderRadius: "5px", bgcolor: color + "18" }}>
                                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color }}>{item.frekuensi}&times;</Typography>
                                </Box>
                              </Stack>
                            </Box>
                            <Box sx={{ height: 5, borderRadius: 3, bgcolor: "#f3f4f6", overflow: "hidden" }}>
                              <Box sx={{ height: "100%", borderRadius: 3, width: `${pct}%`, background: `linear-gradient(90deg, ${color}, ${color}99)`, transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)" }} />
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </>
                )}

                {/* Panel: Top Department */}
                {topTab === 1 && (
                  <>
                    <Typography sx={{ fontSize: "0.72rem", color: "#9ca3af", mb: 2 }}>
                      Berdasarkan jumlah pengajuan &middot; {currentYear}
                    </Typography>
                    {loading
                      ? [1, 2, 3, 4].map((i) => <Skeleton key={i} height={30} sx={{ mb: 1.2 }} />)
                      : (data?.top_units || []).length === 0
                      ? <Typography sx={{ fontSize: "0.78rem", color: "#9ca3af" }}>Belum ada data</Typography>
                      : (data?.top_units || []).map((u, i) => {
                          const pct    = Math.round((u.total / (data.top_units[0]?.total || 1)) * 100);
                          const colors = ["#2563eb", "#0ea5e9", "#38bdf8", "#7dd3fc", "#bae6fd"];
                          return (
                            <Box key={i} sx={{ mb: 1.8 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.6 }}>
                                <Typography noWrap sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", maxWidth: "78%" }}>
                                  {u.unit}
                                </Typography>
                                <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#111827" }}>{u.total}</Typography>
                              </Box>
                              <Box sx={{ height: 5, borderRadius: 2, bgcolor: "#f3f4f6" }}>
                                <Box sx={{ height: "100%", borderRadius: 2, bgcolor: colors[i] || "#2563eb", width: `${pct}%`, transition: "width .5s ease" }} />
                              </Box>
                            </Box>
                          );
                        })
                    }
                  </>
                )}
              </Box>
            </Box>

          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}